import { z } from "zod";
import { PaginationSchema } from "../../pagination/response/PaginationResponse";
import { SpecificRule } from "../../../shared/types/rate/SpecificRule";
import { SpecificAmount } from "../../../shared/types/rate/SpecificAmountType";

const Children = z.object({
    id: z.string(),
    name: z.string(),
    min_night: z.number(),
    max_night: z.number(),
    guest_include: z.number(),
    guest_max: z.number(),
    extra_adult: z.number(),
    extra_child: z.number(),
    extra_adult_rate: z.number(),
    extra_child_rate: z.number(),
    specific_rule: z.enum(SpecificRule.map((s) => s.value) as [string, ...string[]]),
    specific_amount_type: z.enum(SpecificAmount.map((s) => s.value) as [string, ...string[]]),
    specific_value: z.number(),
    parent_id: z.string(),
});

const RoomType = z.object({
    id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    no_of_beds: z.number(),
    cut_of_days: z.number(),
    is_free_allotment: z.boolean(),
    is_auto_assign: z.boolean(),
    is_dynamic_pricing: z.boolean(),
    total_rooms: z.number(),
    created_at: z.string(),
    updated_at: z.string(),
    occ_adults: z.number(),
    occ_children: z.number(),
    occ_infants: z.number(),
    default_occupancy: z.number(),
    room_kind: z.string(),
    capacity: z.number(),
});

const DataSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    room_type_id: z.string(),
    parent_id: z.nullable(z.string()),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    min_rate: z.coerce.number(),
    parent_rate: z.nullable(z.coerce.number()),
    rate: z.coerce.number(),
    min_night: z.number(),
    max_night: z.number(),
    guest_include: z.number(),
    guest_max: z.number(),
    extra_adult: z.number(),
    extra_child: z.number(),
    extra_adult_rate: z.coerce.number(),
    extra_child_rate: z.coerce.number(),
    type: z.enum(['link', 'rate']),
    specific_rule: z.nullable(z.enum(SpecificRule.map((s) => s.value) as [string, ...string[]])),
    specific_amount_type: z.nullable(z.enum(SpecificAmount.map((s) => s.value) as [string, ...string[]])),
    specific_value: z.coerce.number(),
    created_at: z.string(),
    updated_at: z.string(),
    inherit_restriction: z.number().min(0).max(1),
    inherit_closed_to_arrival: z.number().min(0).max(1),
    inherit_closed_to_departure: z.number().min(0).max(1),
    inherit_stop_sell: z.number().min(0).max(1),
    inherit_min_stay_arrival: z.number().min(0).max(1),
    inherit_min_stay_through: z.number().min(0).max(1),
    inherit_max_stay: z.number().min(0).max(1),
    room_type: RoomType.optional(),
    children: z.array(Children).optional(),
});

const IndexRatePlanSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.array(DataSchema),
    pagination: PaginationSchema.optional(),
});

export { IndexRatePlanSchema };
export type RatePlanResponse = z.infer<typeof DataSchema>;
export type IndexRatePlanResponse = z.infer<typeof IndexRatePlanSchema>;